/**
 * AttachmentsTab — file capture & OCR / statement parsing tab.
 *
 * Extracted from DocumentModal.tsx to reduce render complexity.
 * Purely presentational: all state and handlers are passed as props.
 *
 * Contains:
 * - File count badges (attached, saved, analyzed, processing, failed)
 * - Upload dropzone with drag-and-drop support
 * - Photo grid with OCR/sales/structured result display
 * - Empty state
 * - Global OCR processing status
 */

import React from 'react';
import { X, FileText, Upload, Loader2, Sparkles, Camera } from 'lucide-react';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import {
  type DocumentPhotoAttachment,
  MAX_ATTACHMENT_COUNT,
  MAX_ATTACHMENT_FILE_SIZE_BYTES,
  MAX_ATTACHMENT_TOTAL_BYTES,
  OCR_SAFE_TOTAL_CONFIDENCE_THRESHOLD,
  SALES_CONFIRM_SIGNAL_CONFIDENCE_THRESHOLD,
  ATTACHMENT_ANALYSIS_ACCEPT_PURCHASE,
  ATTACHMENT_ANALYSIS_ACCEPT_SALES,
  formatAttachmentFileSize,
  isImageLikeAttachment,
  getAttachmentConfidence,
  buildDetectedSignalLabel,
} from '../documentModalHelpers';
import {
  getSalesIntakeSourceLabel,
  getSalesIntakeTargetLabel,
} from '@/lib/finance/salesIntakeAnalysis';

export interface AttachmentsTabProps {
  isActive: boolean;
  isDarkMode: boolean;
  isPurchaseDocument: boolean;
  photoAttachments: DocumentPhotoAttachment[];
  loadingPersistedAttachments: boolean;
  ocrProcessing: boolean;
  isAttachmentDragActive: boolean;
  formatCurrencyWithSymbol: (value: number | string | undefined | null) => string;
  handlePhotoUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleAttachmentDragOver: (e: React.DragEvent<HTMLLabelElement>) => void;
  handleAttachmentDragLeave: (e: React.DragEvent<HTMLLabelElement>) => void;
  handleAttachmentDrop: (e: React.DragEvent<HTMLLabelElement>) => void;
  handleRemovePhoto: (photoId: string) => Promise<void>;
  handleApplyOcrTotal: (photoId: string) => Promise<void>;
  handleApplyOcrDate: (photoId: string) => void;
  handleApplySalesCustomerPo: (photoId: string) => Promise<void>;
  handleRerunAttachmentAnalysis: (photoId: string) => Promise<void>;
}

export function AttachmentsTab({
  isActive,
  isDarkMode,
  isPurchaseDocument,
  photoAttachments,
  loadingPersistedAttachments,
  ocrProcessing,
  isAttachmentDragActive,
  formatCurrencyWithSymbol,
  handlePhotoUpload,
  handleAttachmentDragOver,
  handleAttachmentDragLeave,
  handleAttachmentDrop,
  handleRemovePhoto,
  handleApplyOcrTotal,
  handleApplyOcrDate,
  handleApplySalesCustomerPo,
  handleRerunAttachmentAnalysis,
}: AttachmentsTabProps) {
  if (!isActive) return null;

  return (
    <div className={`p-6 ${isDarkMode ? 'bg-gray-800/50' : 'bg-gray-50'}`} data-tour-id="work-item-attachments-tab">
      <div className="mb-6">
        <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          Files & OCR
        </h3>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {isPurchaseDocument
            ? 'Drop receipt images, PDFs, spreadsheets, or structured bank files here and apply detected totals or dates back into the document without retyping.'
            : 'Drop customer POs, quote requests, spreadsheets, or other sales documents here and apply detected customer PO references, requested dates, or totals back into the document without retyping.'}
        </p>
        <p className={`mt-2 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
          Saved attachments stay linked to this document after you save. Limits: {MAX_ATTACHMENT_COUNT} files, {formatAttachmentFileSize(MAX_ATTACHMENT_FILE_SIZE_BYTES)} each, {formatAttachmentFileSize(MAX_ATTACHMENT_TOTAL_BYTES)} total.
        </p>
      </div>

      <div className={`mb-4 flex flex-wrap items-center gap-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        <span className={`rounded-full px-3 py-1 ${isDarkMode ? 'bg-gray-900 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
          {photoAttachments.length} file{photoAttachments.length === 1 ? '' : 's'} attached
        </span>
        <span className={`rounded-full px-3 py-1 ${isDarkMode ? 'bg-violet-900/30 text-violet-300' : 'bg-violet-50 text-violet-700 border border-violet-100'}`}>
          {photoAttachments.filter((photo) => photo.is_persisted).length} saved
        </span>
        <span className={`rounded-full px-3 py-1 ${isDarkMode ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'}`}>
          {photoAttachments.filter((photo) => (photo.ocr_result || photo.structured_result || photo.sales_result) && !photo.ocr_processing).length} analyzed
        </span>
        <span className={`rounded-full px-3 py-1 ${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700 border border-blue-100'}`}>
          {photoAttachments.filter((photo) => photo.ocr_processing).length} processing
        </span>
        {photoAttachments.some((photo) => photo.upload_status === 'failed') && (
          <span className={`rounded-full px-3 py-1 ${isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700 border border-red-100'}`}>
            {photoAttachments.filter((photo) => photo.upload_status === 'failed').length} need attention
          </span>
        )}
        {loadingPersistedAttachments && (
          <span className={`rounded-full px-3 py-1 inline-flex items-center gap-1.5 ${isDarkMode ? 'bg-slate-900 text-slate-300' : 'bg-slate-100 text-slate-700 border border-slate-200'}`}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading saved files…
          </span>
        )}
      </div>

      {/* Upload Button */}
      <div className="mb-6">
        <label
          className={`flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors ${isAttachmentDragActive
            ? isDarkMode
              ? 'border-emerald-400 bg-emerald-900/25'
              : 'border-emerald-500 bg-emerald-50'
            : isDarkMode
              ? 'border-gray-600 hover:border-emerald-500 hover:bg-emerald-900/20'
              : 'border-gray-300 hover:border-emerald-500 hover:bg-emerald-50'
            }`}
          onDragEnter={handleAttachmentDragOver}
          onDragOver={handleAttachmentDragOver}
          onDragLeave={handleAttachmentDragLeave}
          onDrop={handleAttachmentDrop}
        >
          <Upload className={`h-6 w-6 ${isAttachmentDragActive ? 'text-emerald-500' : 'text-emerald-600'}`} />
          <div>
            <div className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              {isAttachmentDragActive ? 'Drop files here to queue analysis' : 'Browse files or drag them straight into the dropzone'}
            </div>
            <div className={`mt-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {isPurchaseDocument
                ? 'Multiple files supported. Images use receipt OCR, while PDF, CSV, OFX, QFX, QIF, MT940, TXT, XLSX, XLS, and ZIP files use structured parsing.'
                : 'Multiple files supported. Images use OCR plus sales heuristics, while PDF, TXT, XLSX, XLS, CSV, DOCX, JSON, XML, Markdown, YAML, HTML, LOG, and ZIP files are mapped into sales intake signals. Text-based PDFs work best.'}
            </div>
          </div>
          <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${isDarkMode ? 'bg-white/10 text-white' : 'bg-white text-emerald-700 shadow-sm'}`}>
            Browse files
          </span>
          <input
            type="file"
            accept={isPurchaseDocument ? ATTACHMENT_ANALYSIS_ACCEPT_PURCHASE : ATTACHMENT_ANALYSIS_ACCEPT_SALES}
            multiple
            onChange={handlePhotoUpload}
            className="hidden"
            disabled={ocrProcessing || loadingPersistedAttachments}
          />
        </label>
      </div>

      {loadingPersistedAttachments && photoAttachments.length === 0 && (
        <div className={`mb-6 flex items-center justify-center gap-2 rounded-xl border px-4 py-6 text-sm ${isDarkMode ? 'border-gray-700 bg-gray-900/40 text-gray-300' : 'border-gray-200 bg-white text-gray-700'}`}>
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading saved attachments…
        </div>
      )}

      {/* Photo Grid */}
      {photoAttachments.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {photoAttachments.map((photo) => {
            const previewSource = photo.data_url || photo.file_path;
            const attachmentConfidence = getAttachmentConfidence(photo);
            const fileSize = photo.file_size ?? photo.file_blob?.size ?? 0;
            const uploadStatusLabel = photo.upload_status === 'uploaded'
              ? 'Saved to document'
              : photo.upload_status === 'uploading'
                ? 'Saving…'
                : photo.upload_status === 'failed'
                  ? 'Needs retry'
                  : 'Pending save';

            return (
              <div
                key={photo.id}
                className={`relative rounded-lg overflow-hidden border ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'
                  }`}
              >
                {previewSource && isImageLikeAttachment(photo.file_name, photo.mime_type) ? (
                  <img
                    src={previewSource}
                    alt={photo.file_name}
                    className="w-full h-48 object-cover"
                  />
                ) : (
                  <div className={`flex h-48 flex-col items-center justify-center px-4 text-center ${isDarkMode ? 'bg-gray-900 text-gray-200' : 'bg-gray-100 text-gray-700'}`}>
                    <FileText className="h-10 w-10 mb-3 opacity-70" />
                    <p className="text-xs font-semibold uppercase tracking-[0.18em]">
                      {photo.file_type || 'FILE'}
                    </p>
                    <p className="mt-2 line-clamp-2 text-xs opacity-80">{photo.file_name}</p>
                  </div>
                )}

                {/* OCR Processing Indicator */}
                {photo.ocr_processing && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <div className="text-center">
                      <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
                      <p className="text-white text-sm font-medium">Analyzing file…</p>
                    </div>
                  </div>
                )}

                {/* OCR Success Badge */}
                {(photo.ocr_result || photo.structured_result || photo.sales_result) && !photo.ocr_processing && (
                  <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${photo.sales_result ? 'bg-pink-600 text-white' : photo.ocr_result ? 'bg-emerald-500 text-white' : 'bg-blue-500 text-white'}`}>
                    <Sparkles className="h-3 w-3" />
                    {photo.sales_result ? 'Sales mapped' : photo.ocr_result ? 'AI Complete' : 'Parsed'}
                  </div>
                )}

                {/* Remove Button */}
                <button
                  type="button"
                  onClick={() => void handleRemovePhoto(photo.id)}
                  className="absolute top-2 left-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={photo.ocr_processing || photo.upload_status === 'uploading'}
                >
                  <X className="h-4 w-4" />
                </button>

                {/* Photo Info */}
                <div className={`p-3 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className={`text-xs truncate font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {photo.file_name}
                      </p>
                      <p className={`mt-0.5 text-[10px] uppercase tracking-wide ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        {photo.file_type || 'file'}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                          {formatAttachmentFileSize(fileSize)}
                        </span>
                        <span className={`rounded-full px-2 py-0.5 text-[10px] ${photo.upload_status === 'failed'
                          ? (isDarkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-700 border border-red-100')
                          : photo.upload_status === 'uploaded'
                            ? (isDarkMode ? 'bg-violet-900/30 text-violet-300' : 'bg-violet-50 text-violet-700 border border-violet-100')
                            : (isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-slate-100 text-slate-700 border border-slate-200')}
                        `}>
                          {uploadStatusLabel}
                        </span>
                        {attachmentConfidence != null && (
                          <span className={`rounded-full px-2 py-0.5 text-[10px] ${isDarkMode ? 'bg-emerald-900/30 text-emerald-300' : 'bg-emerald-50 text-emerald-700 border border-emerald-100'}`}>
                            {buildDetectedSignalLabel(attachmentConfidence)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {photo.upload_error && (
                    <div className={`mt-2 rounded-lg px-2 py-1.5 text-[11px] ${isDarkMode ? 'bg-red-900/30 text-red-200' : 'bg-red-50 text-red-700 border border-red-100'}`}>
                      {photo.upload_error}
                    </div>
                  )}

                  {photo.analysis_error && (
                    <div className={`mt-2 rounded-lg px-2 py-1.5 text-[11px] ${isDarkMode ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
                      {photo.analysis_error}
                    </div>
                  )}

                  {photo.sales_result && (
                    <div className="mt-2 space-y-2">
                      <div className="grid grid-cols-2 gap-2 text-[11px]">
                        <div className={`rounded-lg px-2 py-1.5 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                          <span className="block text-[10px] uppercase tracking-wide opacity-70">Detected source</span>
                          <span className="font-semibold">{getSalesIntakeSourceLabel(photo.sales_result.source_document_type)}</span>
                        </div>
                        <div className={`rounded-lg px-2 py-1.5 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                          <span className="block text-[10px] uppercase tracking-wide opacity-70">Suggested next</span>
                          <span className="font-semibold text-pink-600">{getSalesIntakeTargetLabel(photo.sales_result.recommended_target)}</span>
                        </div>
                        <div className={`rounded-lg px-2 py-1.5 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                          <span className="block text-[10px] uppercase tracking-wide opacity-70">Customer PO</span>
                          <span className="font-semibold">{photo.sales_result.customer_po_number || 'Not found'}</span>
                        </div>
                        <div className={`rounded-lg px-2 py-1.5 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                          <span className="block text-[10px] uppercase tracking-wide opacity-70">Requested date</span>
                          <span className="font-semibold">{photo.sales_result.requested_date || 'Not found'}</span>
                        </div>
                      </div>

                      <div className="space-y-1 text-[11px]">
                        {photo.sales_result.customer_name && (
                          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                            <span className="font-semibold">Customer:</span> {photo.sales_result.customer_name}
                          </p>
                        )}
                        {photo.sales_result.total_amount != null && (
                          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                            <span className="font-semibold">Detected total:</span> {formatCurrencyWithSymbol(photo.sales_result.total_amount)}
                          </p>
                        )}
                        {attachmentConfidence != null && attachmentConfidence < SALES_CONFIRM_SIGNAL_CONFIDENCE_THRESHOLD && (
                          <p className={isDarkMode ? 'text-amber-300' : 'text-amber-700'}>
                            <span className="font-semibold">Review:</span> This is a low-confidence signal, so double-check before applying it.
                          </p>
                        )}
                        {photo.sales_result.lines.length > 0 && (
                          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                            {photo.sales_result.lines.length} line item{photo.sales_result.lines.length === 1 ? '' : 's'} extracted from this file.
                          </p>
                        )}
                        {photo.sales_result.text_preview && (
                          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                            {photo.sales_result.text_preview}
                          </p>
                        )}
                        {photo.sales_result.warnings[0] && (
                          <p className={isDarkMode ? 'text-amber-300' : 'text-amber-700'}>
                            <span className="font-semibold">Note:</span> {photo.sales_result.warnings[0]}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {photo.sales_result.customer_po_number && (
                          <button
                            type="button"
                            onClick={() => void handleApplySalesCustomerPo(photo.id)}
                            className={`inline-flex items-center rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${isDarkMode ? 'bg-pink-900/30 text-pink-300 hover:bg-pink-900/50' : 'bg-pink-50 text-pink-700 hover:bg-pink-100'}`}
                          >
                            Apply PO
                          </button>
                        )}
                        {photo.sales_result.requested_date && (
                          <button
                            type="button"
                            onClick={() => handleApplyOcrDate(photo.id)}
                            className={`inline-flex items-center rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${isDarkMode ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                          >
                            Apply requested date
                          </button>
                        )}
                        {photo.sales_result.total_amount != null && (
                          <button
                            type="button"
                            onClick={() => void handleApplyOcrTotal(photo.id)}
                            className={`inline-flex items-center rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${isDarkMode ? 'bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                          >
                            Apply total
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => void handleRerunAttachmentAnalysis(photo.id)}
                          className={`inline-flex items-center rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'}`}
                        >
                          Re-run analysis
                        </button>
                      </div>
                    </div>
                  )}

                  {photo.ocr_result && !photo.sales_result && (
                    <div className="mt-2 space-y-2">
                      <div className="grid grid-cols-2 gap-2 text-[11px]">
                        <div className={`rounded-lg px-2 py-1.5 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                          <span className="block text-[10px] uppercase tracking-wide opacity-70">Detected total</span>
                          <span className="font-semibold text-emerald-600">
                            {photo.ocr_result.total_amount != null
                              ? formatCurrencyWithSymbol(photo.ocr_result.total_amount)
                              : 'Not found'}
                          </span>
                        </div>
                        <div className={`rounded-lg px-2 py-1.5 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                          <span className="block text-[10px] uppercase tracking-wide opacity-70">Detected date</span>
                          <span className="font-semibold">
                            {photo.ocr_result.date || 'Not found'}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-1 text-[11px]">
                        {photo.ocr_result.vendor_name && (
                          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                            <span className="font-semibold">Vendor:</span> {photo.ocr_result.vendor_name}
                          </p>
                        )}
                        {photo.ocr_result.validation?.message && (
                          <p className={isDarkMode ? 'text-amber-300' : 'text-amber-700'}>
                            <span className="font-semibold">Validation:</span> {photo.ocr_result.validation.message}
                          </p>
                        )}
                        {attachmentConfidence != null && attachmentConfidence < OCR_SAFE_TOTAL_CONFIDENCE_THRESHOLD && (
                          <p className={isDarkMode ? 'text-amber-300' : 'text-amber-700'}>
                            <span className="font-semibold">Review:</span> This total should be checked before you apply it.
                          </p>
                        )}
                        {photo.ocr_result.items && photo.ocr_result.items.length > 0 && (
                          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                            {photo.ocr_result.items.length} line item{photo.ocr_result.items.length === 1 ? '' : 's'} detected from this image.
                          </p>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => void handleApplyOcrTotal(photo.id)}
                          className={`inline-flex items-center rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${isDarkMode ? 'bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                        >
                          Apply total
                        </button>
                        <button
                          type="button"
                          onClick={() => handleApplyOcrDate(photo.id)}
                          className={`inline-flex items-center rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${isDarkMode ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                        >
                          Use date
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleRerunAttachmentAnalysis(photo.id)}
                          className={`inline-flex items-center rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'}`}
                        >
                          Re-run OCR
                        </button>
                      </div>
                    </div>
                  )}

                  {photo.structured_result && (
                    <div className="mt-2 space-y-2">
                      <div className="grid grid-cols-2 gap-2 text-[11px]">
                        <div className={`rounded-lg px-2 py-1.5 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                          <span className="block text-[10px] uppercase tracking-wide opacity-70">Format</span>
                          <span className="font-semibold">{photo.structured_result.format}</span>
                        </div>
                        <div className={`rounded-lg px-2 py-1.5 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                          <span className="block text-[10px] uppercase tracking-wide opacity-70">Parsed rows</span>
                          <span className="font-semibold">{photo.structured_result.parsed_rows}/{photo.structured_result.total_rows}</span>
                        </div>
                        <div className={`rounded-lg px-2 py-1.5 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                          <span className="block text-[10px] uppercase tracking-wide opacity-70">Statement period</span>
                          <span className="font-semibold">
                            {(photo.structured_result.start_date && photo.structured_result.end_date)
                              ? `${photo.structured_result.start_date} → ${photo.structured_result.end_date}`
                              : (photo.structured_result.end_date || photo.structured_result.start_date || 'Not found')}
                          </span>
                        </div>
                        <div className={`rounded-lg px-2 py-1.5 ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-white text-gray-700 border border-gray-200'}`}>
                          <span className="block text-[10px] uppercase tracking-wide opacity-70">Closing balance</span>
                          <span className="font-semibold text-blue-600">
                            {photo.structured_result.closing_balance != null
                              ? formatCurrencyWithSymbol(photo.structured_result.closing_balance)
                              : 'Not found'}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-1 text-[11px]">
                        {photo.structured_result.bank && (
                          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>
                            <span className="font-semibold">Detected bank:</span> {photo.structured_result.bank}
                          </p>
                        )}
                        {photo.structured_result.warnings[0] && (
                          <p className={isDarkMode ? 'text-amber-300' : 'text-amber-700'}>
                            <span className="font-semibold">Note:</span> {photo.structured_result.warnings[0]}
                          </p>
                        )}
                        {photo.structured_result.rows.length > 0 && (
                          <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                            {photo.structured_result.rows.slice(0, 2).map((row) => row.description).filter(Boolean).join(' • ')}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {(photo.structured_result.end_date || photo.structured_result.start_date) && (
                          <button
                            type="button"
                            onClick={() => handleApplyOcrDate(photo.id)}
                            className={`inline-flex items-center rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${isDarkMode ? 'bg-blue-900/30 text-blue-300 hover:bg-blue-900/50' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                          >
                            Use file date
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => void handleRerunAttachmentAnalysis(photo.id)}
                          className={`inline-flex items-center rounded-lg px-2.5 py-1.5 text-[11px] font-semibold transition-colors ${isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-100'}`}
                        >
                          Re-run analysis
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {photoAttachments.length === 0 && !loadingPersistedAttachments && (
        <div className={`text-center py-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          <Camera className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No files attached yet</p>
          <p className="text-xs mt-1">
            {isPurchaseDocument
              ? 'Drop images, PDFs, spreadsheets, or structured bank files here to start analysis instantly and keep them linked to the document.'
              : 'Drop customer purchase orders, quote requests, PDFs, spreadsheets, or other sales documents here to start mapping sales context instantly and keep them linked to the document.'}
          </p>
        </div>
      )}

      {/* OCR Processing Global Status */}
      {ocrProcessing && (
        <div className={`mt-4 p-3 rounded-lg ${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Processing uploaded files…</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default AttachmentsTab;
